/**
 * The arithmetic behind sizing the screenshot annotation canvas.
 *
 * It lives apart from the component so it can be tested without a browser:
 * numbers go in and numbers come out, and nothing here touches the DOM.
 */

/** How much bigger the stored image is than the box it is displayed in. */
export interface CanvasDrawingScale {
  x: number;
  y: number;
}

/**
 * The factors the canvas drawing context is scaled by, so that a pointer
 * position measured in displayed (CSS) pixels lands on the right pixel of the
 * stored image.
 *
 * The canvas holds the screenshot at its natural size, and CSS then shrinks it
 * to fit the space on screen. A finger or mouse reports where it touched the
 * shrunken picture, so every drawing coordinate has to be multiplied back up.
 *
 * The two factors always come out the same number, because the displayed height
 * is the displayed width times the image's own aspect ratio. Both are returned
 * anyway, and computed the way the canvas is actually scaled, so the two axes
 * stay independent if the fitting ever changes.
 */
export function drawingScaleForDisplayedWidth(
  imageNaturalWidth: number,
  imageNaturalHeight: number,
  displayedWidthPx: number,
): CanvasDrawingScale {
  const aspectRatio = imageNaturalHeight / imageNaturalWidth;
  return {
    x: imageNaturalWidth / displayedWidthPx,
    y: imageNaturalHeight / (displayedWidthPx * aspectRatio),
  };
}

/** The CSS limits that keep the canvas clear of the form sitting below it. */
export interface ScreenshotCanvasFit {
  maxWidthPx: number;
  maxHeightPx: number;
}

/**
 * How large the canvas may be drawn without sliding under the form that floats
 * at the bottom of the editor.
 *
 * The height it may take is simply the gap between the top of the canvas and
 * the top of the form. The width follows from that height, because the picture
 * keeps its shape: a picture twice as wide as it is tall may be twice as wide
 * as the height it was given.
 *
 * Both positions are page coordinates, of the kind `getBoundingClientRect()`
 * reports, and both are measured after the canvas has been given the image.
 */
export function fitCanvasAboveForm(
  imageNaturalWidth: number,
  imageNaturalHeight: number,
  canvasTopPx: number,
  formTopPx: number,
): ScreenshotCanvasFit {
  const aspectRatio = imageNaturalHeight / imageNaturalWidth;
  const maxHeightPx = formTopPx - canvasTopPx;
  return {
    maxHeightPx,
    maxWidthPx: maxHeightPx / aspectRatio,
  };
}
